import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { create } from "zustand";

import { api } from "../../api/client";
import type { DeployedUnit, Deployment, MissionSymbol, Phase, SubordinateUnit, Subordinates } from "../../api/types";
import { k } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { entryZoom } from "../recenter";
import { isManeuverUnit } from "./maneuverUnits";
import { provideSymbolImage, symbolId } from "./symbols";

/** Niveaux inférieurs de la carte zoomée (registres J11 à J14, J16 ; demande d'Alexis du 17/10/2026, reprise le
 *  23/10/2026 : « tu optimiseras le zoom sur les unités avec les niveaux N-1... et vice versa, l'actuelle logique ne
 *  fonctionne pas très bien »). En zoomant, les unités affichées laissent place à leurs subordonnés, puis aux leurs,
 *  jusqu'à la section ; en dézoomant, elles se regroupent. Les positions viennent du serveur : ce sont celles des
 *  cartes des OPO déclinés (dossiers existants, sinon tels que la déclinaison les créerait).
 *
 *  Choix du niveau (registre J16) : un seul niveau à l'écran (registre J13), choisi d'après l'écartement réel, à
 *  l'écran, des unités que l'on regarde — celles de la vue — et non plus d'après des seuils de zoom fixes tirés de
 *  tout le dispositif. On descend d'un niveau quand ses unités de la vue sont à `EXPAND_PX` les unes des autres (trois
 *  sur quatre au moins), on remonte quand celles du niveau affiché se serrent sous `COLLAPSE_PX` (hystérésis) ; jamais
 *  à contresens du geste (zoom avant : on ne remonte pas ; arrière : on ne descend pas), ni en retour sur un
 *  changement de moins de `DWELL_MS` (anti-rebond) ; un déplacement de la carte réévalue à son arrêt. Tous les niveaux
 *  sont demandés d'un coup au serveur, qui les calcule d'avance ; tant que le suivant se prépare, l'attente est dite
 *  et le niveau courant reste. Un niveau peut être fixé à la main (repère « Niveau affiché »). */

/** Écart à l'écran (px) entre symboles voisins d'un niveau pour qu'il s'affiche : symbole d'environ 49 px
 *  (`symbols.ts`) et une marge. Mesuré sur le premier quartile des unités de la vue (trois sur quatre au moins ont ce
 *  champ libre). */
export const EXPAND_PX = 60;
/** On remonte d'un niveau quand ses unités se serrent sous cet écart (hystérésis : environ un demi-niveau de zoom). */
export const COLLAPSE_PX = 42;
/** Le premier niveau inférieur ne s'affiche qu'au-delà du zoom d'entrée du dossier : l'affichage initial est inchangé. */
export const ENTRY_MARGIN = 0.75;
/** Hystérésis du zoom d'entrée au dézoom. */
export const ENTRY_HYSTERESIS = 0.3;
/** Anti-rebond : aucun retour sur un changement de niveau plus récent que ce délai. */
export const DWELL_MS = 450;
/** Déplacement de la carte sans zoom : un retour sur un changement de niveau plus récent que ce délai est refusé (la
 *  vue qui passe d'un secteur serré à un secteur aéré ne fait pas clignoter le niveau). */
export const PAN_DWELL_MS = 1500;
/** Un geste de zoom compte encore ce délai après son dernier mouvement (fin d'animation, réponse du serveur). */
export const GESTURE_MEMORY_MS = 800;
/** Niveau affiché très aéré (le suivant serait lisible) alors qu'il est le dernier prêt : l'attente est dite. Un chef
 *  a souvent trois subordonnés côte à côte : leur écartement vaut environ le tiers du sien. */
export const WAIT_PX = 3 * EXPAND_PX;
/** Marge de la vue (fraction de sa largeur et de sa hauteur) : les unités au bord comptent. */
export const VIEW_MARGIN = 0.1;
/** Moins d'unités que cela dans la vue (on regarde un vide entre deux secteurs) : les plus proches du centre. */
export const MIN_IN_VIEW = 3;
export const NEAREST_FALLBACK = 6;
/** Mètres par pixel au zoom 0 à l'équateur (tuiles de 512 px, comme `recenter.ts`). */
const METERS_PER_PIXEL_Z0 = 78271.517;
const M_PER_DEG = 111_320;

type Point = [number, number];

/** Distance (m) entre deux points [lon, lat], plan local. */
export function distanceM(a: Point, b: Point): number {
  const lat = (((a[1] + b[1]) / 2) * Math.PI) / 180;
  return Math.hypot((a[0] - b[0]) * M_PER_DEG * Math.cos(lat), (a[1] - b[1]) * M_PER_DEG);
}

/** Unités empilées au même point (deux dossiers subordonnés d'appui sur le même emplacement du gabarit) : aucun zoom
 *  ne les sépare, elles ne comptent pas dans l'écartement. */
export const STACKED_M = 50;

/** Temps de la carte : l'écartement vaut pour les trois (registre J14 : changer de temps ne change pas le niveau). */
export const LEVEL_PHASES: readonly Phase[] = ["T1", "T2", "T3"];

/** Unité qui compte dans l'écartement d'un niveau : amie, dessinée, détaillée (repère discret exclu). */
interface SpacingSource {
  key: string;
  side: string;
  hidden?: boolean;
  undetailed?: boolean;
  parent?: string | null;
  arm?: string;
  specialty?: string | null;
  positions: Partial<Record<Phase, { lon: number; lat: number } | undefined>>;
}

/** Point d'écartement : positions de l'unité à chaque temps et distance (m) à sa plus proche voisine du niveau,
 *  médiane des trois temps (voisines empilées ignorées) ; null si elle n'a aucune voisine. */
export interface SpacingPoint {
  key: string;
  at: Partial<Record<Phase, Point>>;
  nn: number | null;
}

/** Distance de chaque point à son plus proche voisin (m, voisins empilés ignorés ; Infinity sans voisin), en plan
 *  local : balayage des points triés par abscisse, qui s'arrête dès que l'écart en abscisse dépasse le meilleur
 *  trouvé (un millier de sections d'un corps en quelques millisecondes, au lieu d'un million de distances). */
function nearestDistances(points: readonly (Point | undefined)[]): number[] {
  const present = points.flatMap((p, i) => (p ? [i] : []));
  const lat0 = present.length ? present.reduce((sum, i) => sum + points[i]![1], 0) / present.length : 0;
  const kx = M_PER_DEG * Math.cos((lat0 * Math.PI) / 180);
  const xy = new Map(present.map((i) => [i, [points[i]![0] * kx, points[i]![1] * M_PER_DEG] as const]));
  const order = [...present].sort((a, b) => xy.get(a)![0] - xy.get(b)![0]);
  const out = points.map(() => Infinity);
  order.forEach((i, rank) => {
    const [x, y] = xy.get(i)!;
    let best = Infinity;
    for (const step of [-1, 1]) {
      for (let r = rank + step; r >= 0 && r < order.length; r += step) {
        const [qx, qy] = xy.get(order[r]!)!;
        if (Math.abs(qx - x) >= best) break;
        const d = Math.hypot(qx - x, qy - y);
        if (d >= STACKED_M && d < best) best = d;
      }
    }
    out[i] = best;
  });
  return out;
}

const pointsCache = new WeakMap<readonly SpacingSource[], SpacingPoint[]>();

/** Points d'écartement d'un niveau (calculés une fois par réponse du serveur et gardés pour elle ; la vue ne fait
 *  ensuite que les filtrer). */
export function spacingPoints(units: readonly SpacingSource[]): SpacingPoint[] {
  const cached = pointsCache.get(units);
  if (cached) return cached;
  // Unités d'un niveau inférieur : les subordonnés seulement (les unités gardées de la carte du dossier, sans
  // parent, n'en font pas partie) ; unités du dossier (sans champ `parent`) : toutes. Unités de mêlée seulement :
  // celles que la carte montre par défaut (appuis, soutiens, PC et trains paraissent avec « Appuis et soutien »).
  const kept = units.filter(
    (u) => u.side === "AMI" && !u.hidden && !u.undetailed && u.parent !== null && isManeuverUnit({ arm: u.arm ?? "", specialty: u.specialty ?? null }),
  );
  const at = kept.map((u) => {
    const out: Partial<Record<Phase, Point>> = {};
    for (const ph of LEVEL_PHASES) {
      const p = u.positions[ph];
      if (p) out[ph] = [p.lon, p.lat];
    }
    return out;
  });
  const perPhase = LEVEL_PHASES.map((ph) => nearestDistances(at.map((a) => a[ph])));
  const result = kept.map((u, i) => {
    const values = perPhase.map((d) => d[i]!).filter((d) => Number.isFinite(d));
    values.sort((a, b) => a - b);
    const m = Math.floor(values.length / 2);
    const nn = values.length ? (values.length % 2 ? values[m]! : Math.sqrt(values[m - 1]! * values[m]!)) : null;
    return { key: u.key, at: at[i]!, nn };
  });
  pointsCache.set(units, result);
  return result;
}

/** Emprise de la vue (degrés). */
export interface View {
  west: number;
  south: number;
  east: number;
  north: number;
}

function pointAt(p: SpacingPoint, phase: Phase): Point | undefined {
  return p.at[phase] ?? p.at.T1 ?? p.at.T2 ?? p.at.T3;
}

/** Écartement (m) des unités d'un niveau que l'on regarde : premier quartile des distances à la plus proche voisine
 *  des unités de la vue (marge `VIEW_MARGIN`) ; s'il y en a moins de `MIN_IN_VIEW`, des `NEAREST_FALLBACK` plus
 *  proches du centre de la vue. null sans aucune unité mesurable. */
export function viewSpacingM(points: readonly SpacingPoint[], view: View, phase: Phase): number | null {
  const measurable = points.filter((p) => p.nn !== null && pointAt(p, phase));
  if (!measurable.length) return null;
  const dx = (view.east - view.west) * VIEW_MARGIN;
  const dy = (view.north - view.south) * VIEW_MARGIN;
  let chosen = measurable.filter((p) => {
    const [lon, lat] = pointAt(p, phase)!;
    return lon >= view.west - dx && lon <= view.east + dx && lat >= view.south - dy && lat <= view.north + dy;
  });
  if (chosen.length < MIN_IN_VIEW) {
    const center: Point = [(view.west + view.east) / 2, (view.south + view.north) / 2];
    chosen = [...measurable]
      .sort((a, b) => distanceM(pointAt(a, phase)!, center) - distanceM(pointAt(b, phase)!, center))
      .slice(0, NEAREST_FALLBACK);
  }
  const values = chosen.map((p) => p.nn!).sort((a, b) => a - b);
  return values[Math.floor((values.length - 1) / 4)] ?? null;
}

/** Mètres par pixel au zoom `zoom`, à la latitude `lat`. */
export function metersPerPixel(zoom: number, lat: number): number {
  return (METERS_PER_PIXEL_Z0 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
}

/** Écartement à l'écran (px) des unités d'un niveau que l'on regarde ; null sans unité mesurable. */
export function viewSpacingPx(points: readonly SpacingPoint[], view: View, phase: Phase, zoom: number): number | null {
  const meters = viewSpacingM(points, view, phase);
  if (meters === null) return null;
  return meters / metersPerPixel(zoom, (view.south + view.north) / 2);
}

/** Dernier changement de niveau (anti-rebond). */
export interface LevelChange {
  at: number;
  from: number;
  to: number;
}

export interface DepthInput {
  zoom: number;
  view: View;
  phase: Phase;
  /** Points d'écartement : index 0, unités du dossier ; index d, unités de la profondeur d chargée. */
  points: readonly (readonly SpacingPoint[])[];
  /** Profondeurs chargées. */
  loaded: number;
  /** Rien plus bas que la dernière profondeur chargée. */
  complete: boolean;
  /** Zoom d'entrée du dossier. */
  entry: number;
  current: number;
  /** Sens du geste : 1 zoom avant, -1 zoom arrière, 0 déplacement (ou rien). */
  direction: -1 | 0 | 1;
  now: number;
  last: LevelChange | null;
  /** Niveau fixé à la main (null : selon le zoom). */
  manual: number | null;
}

export interface DepthChoice {
  depth: number;
  /** Le niveau suivant serait lisible mais se prépare encore : l'attente est dite (le niveau courant reste). */
  waiting: boolean;
}

export const MAX_DEPTH = 4;

/** Profondeur à afficher (fonction pure, registre J16 ; voir l'en-tête du module). */
export function chooseDepth(input: DepthInput): DepthChoice {
  const { zoom, view, phase, points, loaded, complete, entry, current, direction, now, last, manual } = input;
  const more = !complete && loaded < MAX_DEPTH;
  if (manual !== null) {
    const depth = Math.max(0, Math.min(manual, loaded));
    return { depth, waiting: manual > loaded && more };
  }
  const px = (depth: number) => (points[depth] ? viewSpacingPx(points[depth]!, view, phase, zoom) : null);
  // Retour en arrière sur un changement trop récent (pincement qui hésite, unités qui entrent dans la vue).
  const dwell = direction === 0 ? PAN_DWELL_MS : DWELL_MS;
  const bounce = (to: number) =>
    last !== null && now - last.at < dwell && Math.sign(to - last.to) === Math.sign(last.from - last.to);
  let depth = Math.max(0, Math.min(current, loaded));
  if (direction >= 0) {
    while (depth < loaded) {
      if (depth === 0 && zoom < entry + ENTRY_MARGIN) break;
      const next = px(depth + 1);
      if (next === null || next < EXPAND_PX || bounce(depth + 1)) break;
      depth += 1;
    }
  }
  if (depth === Math.min(current, loaded) && direction <= 0) {
    while (depth > 0) {
      const here = px(depth);
      const tight = here !== null && here < COLLAPSE_PX;
      const belowEntry = depth === 1 && zoom < entry + ENTRY_MARGIN - ENTRY_HYSTERESIS;
      if ((!tight && !belowEntry) || bounce(depth - 1)) break;
      depth -= 1;
    }
  }
  const spread = px(depth);
  const waiting =
    more && depth === loaded && spread !== null && spread >= WAIT_PX && (loaded > 0 || zoom >= entry + ENTRY_MARGIN);
  return { depth, waiting };
}

/** Libellé d'une profondeur : l'échelon le plus fréquent de ses unités amies détaillées (repères exclus). */
const ECHELON_LEVELS: Record<string, string> = {
  CORPS: k("Corps d'armée"),
  DIVISION: k("Divisions"),
  BRIGADE: k("Brigades"),
  REGIMENT: k("GTIA"),
  BATAILLON: k("GTIA"),
  COMPAGNIE: k("S/GTIA"),
  SECTION: k("Sections"),
  GROUPE: k("Groupes"),
};

/** Unités montrées d'un niveau (profondeur 0 : le dispositif du dossier, sans parent). */
interface LabelSource {
  side: string;
  echelon: string | null;
  undetailed?: boolean;
  standing_task?: string | null;
}

export function levelLabel(units: readonly (LabelSource & { parent?: string | null })[], top = false): string | null {
  const counts = new Map<string, number>();
  for (const u of units) {
    if (u.side !== "AMI" || (!top && !u.parent) || !u.echelon || u.undetailed || u.standing_task) continue;
    counts.set(u.echelon, (counts.get(u.echelon) ?? 0) + 1);
  }
  let best: string | null = null;
  let most = 0;
  for (const [echelon, n] of counts) {
    if (n > most) [best, most] = [echelon, n];
  }
  return best ? (ECHELON_LEVELS[best] ?? null) : null;
}

/** Décalage [dlon, dlat] à appliquer aux subordonnés d'une unité de la carte du dossier : de sa position au
 *  moment du calcul (`roots`) à sa position affichée (déplacée depuis, en cours de glisser-déposer). */
export function rootShift(roots: Subordinates["roots"], root: string, phase: Phase, shown: Point | undefined): Point {
  const at = roots[root]?.[phase];
  if (!at || !shown) return [0, 0];
  return [shown[0] - at[0], shown[1] - at[1]];
}

// --- État partagé : profondeur affichée et unités à montrer ------------------------------------------------------

/** Niveau proposé au choix manuel : profondeur et libellé. */
export interface LevelOption {
  depth: number;
  label: string;
}

interface ZoomLevelsState {
  depth: number;
  /** Unités montrées à la profondeur affichée (null : celles du dispositif du dossier). */
  units: SubordinateUnit[] | null;
  /** Symboles de mission de la profondeur affichée, par temps (null : ceux du dispositif du dossier). */
  symbols: Partial<Record<Phase, MissionSymbol[]>> | null;
  roots: Subordinates["roots"];
  /** Libellé du niveau affiché (profondeur 0 comprise, dès qu'un niveau inférieur existe ou se prépare). */
  label: string | null;
  /** Le zoom demande une profondeur que le serveur prépare encore. */
  waiting: boolean;
  /** Niveaux proposés au choix manuel (profondeur 0 comprise). */
  options: LevelOption[];
  /** Niveau fixé à la main (null : selon le zoom). */
  manual: number | null;
  /** Profondeurs chargées (diagnostic, mesure). */
  loaded: number;
  setManual: (depth: number | null) => void;
  set: (next: Partial<Omit<ZoomLevelsState, "set" | "setManual">>) => void;
}

export const useZoomLevels = create<ZoomLevelsState>((set) => ({
  depth: 0,
  units: null,
  symbols: null,
  roots: {},
  label: null,
  waiting: false,
  options: [],
  manual: null,
  loaded: 0,
  setManual: (manual) => set({ manual }),
  set: (next) => set(next),
}));
// Accès de diagnostic en développement uniquement (mesure du zoom, tools/qa/mesure_zoom_navigateur.py).
if (import.meta.env.DEV && typeof window !== "undefined")
  (window as unknown as { __gxZoomLevels?: unknown }).__gxZoomLevels = useZoomLevels;

const PENDING_POLL_MS = 1200;
const RETRY_POLL_MS = 65_000;
/** Échec du calcul (rendu une fois par le serveur) : redemandé plus tard, sans rien effacer de l'affichage. */
const FAILED_POLL_MS = 20_000;

/** Réponse prête gardée entre deux requêtes : la plus profonde de la version du dossier ; une autre version ne la
 *  remplace qu'une fois prête au moins jusqu'à la profondeur affichée (`need`) ou complète — pendant le calcul d'une
 *  nouvelle version, les niveaux d'avant restent à l'écran, leurs unités ramenées auprès de leur chef par `roots`
 *  (jamais de niveau qui disparaît le temps du calcul). */
export function keepDeepest(
  kept: Subordinates | undefined,
  answer: Subordinates | undefined,
  need = 0,
): Subordinates | undefined {
  if (answer?.status !== "ready") return kept;
  if (!kept) return answer;
  if (kept.version === answer.version) return kept.levels.length > answer.levels.length ? kept : answer;
  if (answer.complete || answer.levels.length >= Math.min(need, kept.levels.length)) return answer;
  return kept;
}

/** Carte du moteur, telle que le pilote la lit (Mapbox GL JS ou MapLibre GL JS). */
export interface ZoomMap {
  getZoom(): number;
  getBounds(): { getWest(): number; getSouth(): number; getEast(): number; getNorth(): number } | null;
  on(type: "moveend", listener: () => void): unknown;
  off(type: "moveend", listener: () => void): unknown;
}

function viewOf(map: ZoomMap): View | null {
  const b = map.getBounds();
  return b ? { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() } : null;
}

/** Charge les niveaux inférieurs du dossier enregistré et choisit la profondeur selon la vue (carte, `MapCanvas`).
 *  Brouillon non enregistré, CENTAC, compagnie ou carte sans dispositif : rien (profondeur 0). */
export function useZoomLevelsDriver(zoom: number, deployment: Deployment | undefined, getMap: () => ZoomMap | null | undefined) {
  const exerciseId = useUi((s) => s.deploymentParams?.exercise_id ?? null);
  const phase = useUi((s) => s.phase);
  const manual = useZoomLevels((s) => s.manual);
  const set = useZoomLevels((s) => s.set);
  const version = deployment?.exercise_version ?? null;
  const enabled = Boolean(exerciseId && deployment && deployment.level !== "CENTAC" && deployment.level !== "COMPAGNIE");
  // Tous les niveaux d'un coup : le serveur les calcule d'avance et rend ceux qui sont faits (`preparing`).
  const query = useQuery({
    queryKey: ["subordinates", exerciseId, version, MAX_DEPTH] as const,
    queryFn: () => api.exerciseSubordinates(exerciseId!, MAX_DEPTH),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (q.state.status === "error") return FAILED_POLL_MS;
      if (data?.status === "pending" || data?.preparing) return PENDING_POLL_MS;
      return data?.problems?.length ? RETRY_POLL_MS : false;
    },
  });
  const depthNow = useRef(0);
  // Réponse prête la plus profonde du dossier : gardée pendant qu'une profondeur plus basse, ou la version suivante
  // du dossier, se prépare.
  const lastReady = useRef<{ exercise: string | null; data: Subordinates } | null>(null);
  const answer = enabled ? query.data : undefined;
  const previous = lastReady.current?.exercise === exerciseId ? lastReady.current.data : undefined;
  const kept = keepDeepest(previous, answer, depthNow.current);
  if (kept && kept !== previous) lastReady.current = { exercise: exerciseId, data: kept };
  const data = enabled ? kept : undefined;
  const levels = useMemo(() => data?.levels ?? [], [data]);
  const preparing = enabled && (answer?.status === "pending" || Boolean(answer?.preparing) || query.status === "pending");
  const complete = Boolean(data?.complete) && !preparing;
  // Points d'écartement de chaque niveau : unités du dossier, puis de chaque profondeur (subordonnés seulement).
  const points = useMemo(() => {
    if (!deployment) return [] as SpacingPoint[][];
    return [
      spacingPoints(deployment.units as readonly DeployedUnit[]),
      ...levels.map((lv) => spacingPoints(lv.units)),
    ];
  }, [deployment, levels]);
  const topLabel = useMemo(() => (deployment ? levelLabel(deployment.units, true) : null), [deployment]);
  const options = useMemo<LevelOption[]>(() => {
    if (!enabled || (!levels.length && !preparing)) return [];
    const out: LevelOption[] = topLabel ? [{ depth: 0, label: topLabel }] : [];
    levels.forEach((lv, i) => {
      const label = levelLabel(lv.units);
      if (label && !out.some((o) => o.label === label)) out.push({ depth: i + 1, label });
    });
    return out;
  }, [enabled, levels, preparing, topLabel]);
  const entry = deployment ? entryZoom(deployment) : 0;
  const memory = useRef<{
    zoom: number | null;
    last: LevelChange | null;
    exercise: string | null;
    phase: Phase | null;
    /** Sens et instant du dernier mouvement de zoom (fin de geste, animation des boutons, réponse arrivée en plein
     *  geste : on reste dans le sens du geste). */
    gesture: { direction: -1 | 1; at: number } | null;
  }>({ zoom: null, last: null, exercise: null, phase: null, gesture: null });

  // Évaluation : à chaque zoom (sens du geste), à l'arrêt d'un déplacement (`pan` : sans sens, sauf geste de zoom
  // récent), à chaque niveau chargé (`data` : on ne peut que descendre — une réponse du serveur ne fait jamais
  // remonter d'un niveau sans geste de l'utilisateur).
  const evaluate = useRef<(trigger: "zoom" | "pan" | "data") => void>(() => {});
  evaluate.current = (trigger: "zoom" | "pan" | "data") => {
    const map = getMap();
    const view = map ? viewOf(map) : null;
    const state = useZoomLevels.getState();
    if (memory.current.exercise !== exerciseId) {
      memory.current = { zoom: null, last: null, exercise: exerciseId, phase: null, gesture: null };
      if (state.manual !== null) state.setManual(null);
    }
    if (!enabled || !view || !deployment) {
      if (state.depth !== 0 || state.units !== null || state.label !== null || state.options.length)
        set({ depth: 0, units: null, symbols: null, roots: {}, label: null, waiting: false, options: [], loaded: 0 });
      depthNow.current = 0;
      return;
    }
    const current = map!.getZoom();
    const before = memory.current.zoom;
    const now = performance.now();
    const moved: -1 | 0 | 1 = before === null ? 0 : current > before + 0.005 ? 1 : current < before - 0.005 ? -1 : 0;
    if (moved !== 0) memory.current.gesture = { direction: moved, at: now };
    const recent = memory.current.gesture && now - memory.current.gesture.at < GESTURE_MEMORY_MS ? memory.current.gesture.direction : 0;
    const direction: -1 | 0 | 1 = moved !== 0 ? moved : recent !== 0 ? recent : trigger === "data" ? 1 : 0;
    // Changer de temps à zoom égal ne change pas le niveau (registre J14) : seules ses unités suivent le temps.
    const phaseOnly = memory.current.phase !== null && memory.current.phase !== phase && trigger !== "pan" && moved === 0;
    memory.current.zoom = current;
    memory.current.phase = phase;
    const choice = phaseOnly
      ? { depth: Math.min(state.depth, levels.length), waiting: state.waiting }
      : chooseDepth({
          zoom: current,
          view,
          phase,
          points,
          loaded: levels.length,
          complete,
          entry,
          current: state.depth,
          direction,
          now,
          last: memory.current.last,
          manual: state.manual,
        });
    if (choice.depth !== state.depth) memory.current.last = { at: now, from: state.depth, to: choice.depth };
    depthNow.current = choice.depth;
    const units = choice.depth > 0 ? (levels[choice.depth - 1]?.units ?? null) : null;
    const label = units ? levelLabel(units) : options.length ? topLabel : null;
    const next = {
      depth: choice.depth,
      units,
      symbols: units ? (levels[choice.depth - 1]?.mission_symbols ?? {}) : null,
      roots: data?.roots ?? {},
      label,
      waiting: choice.waiting,
      options,
      loaded: levels.length,
    };
    if (
      next.depth !== state.depth ||
      next.units !== state.units ||
      next.symbols !== state.symbols ||
      next.roots !== state.roots ||
      next.label !== state.label ||
      next.waiting !== state.waiting ||
      next.options !== state.options ||
      next.loaded !== state.loaded
    )
      set(next);
  };
  useEffect(() => evaluate.current("zoom"), [zoom]);
  useEffect(() => evaluate.current("data"), [points, phase, complete, enabled, data, options]);
  // Retour à « Selon le zoom » : le niveau rejoint le zoom, dans un sens comme dans l'autre.
  useEffect(() => evaluate.current("pan"), [manual]);
  // Déplacement de la carte : la vue change, le niveau est réévalué à l'arrêt — sans sens pour un déplacement pur,
  // dans le sens du geste pour la fin d'un zoom (la fin d'un zoom avant ne fait jamais remonter d'un niveau).
  const map = getMap();
  useEffect(() => {
    if (!map) return;
    const onEnd = () => evaluate.current("pan");
    map.on("moveend", onEnd);
    return () => {
      map.off("moveend", onEnd);
    };
  }, [map]);
  // Images des symboles des niveaux chargés préparées d'avance, par tranches de 8 ms aux moments libres : le premier
  // affichage d'un niveau ne dessine plus ses symboles d'un coup (images de 60 à 170 ms au changement, registre J16).
  useEffect(() => {
    const host = map as unknown as Parameters<typeof provideSymbolImage>[0] | null | undefined;
    if (!host || !levels.length) return;
    const ids = [...new Set(levels.flatMap((lv) => lv.units.map((u) => symbolId(u.sidc, u.designation_left, u.designation_right))))];
    let index = 0;
    let cancelled = false;
    const idle = (cb: () => void) =>
      typeof window.requestIdleCallback === "function" ? window.requestIdleCallback(cb, { timeout: 500 }) : window.setTimeout(cb, 16);
    const slice = () => {
      if (cancelled) return;
      const end = performance.now() + 8;
      while (index < ids.length && performance.now() < end) {
        try {
          provideSymbolImage(host, ids[index]!);
        } catch {
          // Carte en cours de remplacement (changement de fond) : l'image sera faite à la demande.
        }
        index += 1;
      }
      if (index < ids.length) idle(slice);
    };
    idle(slice);
    return () => {
      cancelled = true;
    };
  }, [levels, map]);
  useEffect(
    () => () => set({ depth: 0, units: null, symbols: null, roots: {}, label: null, waiting: false, options: [], manual: null, loaded: 0 }),
    [set],
  );
  return { depth: depthNow.current };
}
